import { useCallback } from "react";
import { collection, doc, getDocs, query, serverTimestamp, setDoc, updateDoc, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useTypeSettings } from "../contexts/TypeSettingsContext";
import { storedEntityTypes, type EntityType, type EventOption, type InteractionEvent } from "../types";
import { typeEventOptions } from "../config/typeSettings";
import { hasActivityFlow } from "../utils/activityFlow";
import { MIN_HISTORY_VISITS, canCopyStepOrder, copyStepOrder } from "../utils/stepOrder";
import { entityTypeLabel } from "../utils/tripDisplay";
import { useEntityEventOptions } from "./useStepLogger";
import type { StepHistory, StepOrderTarget } from "../components/entities/StepOrderSheet";

/** Your own past activities at these places, as their logged steps. */
async function loadActivities(uid: string, entityIds: string[]): Promise<InteractionEvent[][]> {
  const out: InteractionEvent[][] = [];
  // `in` takes at most 30 values.
  for (let i = 0; i < entityIds.length; i += 30) {
    const chunk = entityIds.slice(i, i + 30);
    const snap = await getDocs(
      query(collection(db, "interactions"), where("userId", "==", uid), where("entityId", "in", chunk)),
    );
    snap.forEach((d) => {
      const events = d.data().events as InteractionEvent[] | undefined;
      if (Array.isArray(events) && events.length > 0) out.push(events);
    });
  }
  return out;
}

/**
 * What the button-order sheet needs for one activity's place: where it can
 * save (this place, or every place of its type), how, and where to learn an
 * order from. `null` when you can't change places (no manage_entities role).
 */
export function useStepOrderEditing(
  entityId: string | null | undefined,
  entityName: string | null | undefined,
  type: EntityType | undefined,
) {
  const { user, userProfile } = useAuth();
  const canManage = !!userProfile?.roles?.includes("manage_entities");
  const ownOptions = useEntityEventOptions(entityId);
  const typeSettings = useTypeSettings(type);
  // A place wired by hand keeps its own order whatever the type says.
  const handWired = !!ownOptions && ownOptions.length > 0 && hasActivityFlow(ownOptions);
  const typeLabel = type ? entityTypeLabel(type) : null;
  const typeTargetKey = "type";

  const targets = (editedSteps: Set<string>, draft: EventOption[]): StepOrderTarget[] => {
    const list: StepOrderTarget[] = [];
    if (type && typeLabel) {
      const base = typeEventOptions(type, typeSettings);
      const fits = canCopyStepOrder(draft, base, editedSteps);
      list.push({
        key: typeTargetKey,
        label: `Save for every ${typeLabel}`,
        disabledReason: fits
          ? null
          : `Renamed, added or removed steps can only be saved for this place. To change every ${typeLabel}, edit its steps on the type page.`,
      });
    }
    if (entityId) list.push({ key: "place", label: list.length ? "Just this place" : "Save for this place" });
    return list;
  };

  const save = useCallback(
    async (draft: EventOption[], edited: Set<string>, target: string) => {
      if (target === "place" && entityId) {
        await updateDoc(doc(db, "entities", entityId), { eventOptions: draft, updatedAt: serverTimestamp() });
        return;
      }
      if (target === typeTargetKey && type) {
        const base = typeEventOptions(type, typeSettings);
        await setDoc(
          doc(db, "typeSettings", type),
          { eventOptions: copyStepOrder(draft, base, edited), updatedAt: serverTimestamp() },
          { merge: true },
        );
        // This place wouldn't follow the type, so give it the same order too,
        // or the card you just edited from wouldn't change.
        if (entityId && handWired && ownOptions) {
          await updateDoc(doc(db, "entities", entityId), {
            eventOptions: copyStepOrder(draft, ownOptions, edited),
            updatedAt: serverTimestamp(),
          });
        }
      }
    },
    [entityId, type, typeSettings, handWired, ownOptions],
  );

  const loadHistory = useCallback(async (): Promise<StepHistory[]> => {
    const uid = user?.uid;
    if (!uid) return [];
    const sources: StepHistory[] = [];
    if (entityId) {
      sources.push({ label: entityName || "this place", activities: await loadActivities(uid, [entityId]) });
      if (sources[0]!.activities.length >= MIN_HISTORY_VISITS) return sources;
    }
    if (type && typeLabel) {
      const places = await getDocs(query(collection(db, "entities"), where("type", "in", storedEntityTypes([type]))));
      sources.push({ label: `every ${typeLabel}`, activities: await loadActivities(uid, places.docs.map((d) => d.id)) });
    }
    return sources;
  }, [user?.uid, entityId, entityName, type, typeLabel]);

  const note =
    type && typeLabel
      ? handWired
        ? `${entityName || "This place"} has its own step order, so saving for every ${typeLabel} changes it too.`
        : `“Just this place” gives ${entityName || "it"} its own order, and later changes for every ${typeLabel} won't reach it.`
      : null;

  return canManage ? { targets, save, loadHistory, note } : null;
}
